/**
 * Public access-auth screens: Forgot Password, Reset Password, and Invitation
 * Accept. Focused, AVOS-branded surfaces wired to the real /api/auth/* backend
 * via injected service callbacks (web boundary: no backend import).
 */
import { useEffect, useState, type FormEvent, type ReactElement } from "react";

function getQueryParam(name: string): string {
  if (typeof window === "undefined") return "";
  return new URLSearchParams(window.location.search).get(name) ?? "";
}

/** Forgot Password: enter email, always neutral response. */
export function ForgotPasswordScreen({
  onSubmit,
}: {
  readonly onSubmit: (email: string) => Promise<{ ok: boolean; message: string }>;
}): ReactElement {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault(); setBusy(true);
    const r = await onSubmit(email); setBusy(false); setDone(r.message);
  }
  return (
    <div className="auth-card">
      <h1 className="auth-card__title">Reset your password</h1>
      {done ? (
        <p className="auth-card__note" role="status">{done}</p>
      ) : (
        <form onSubmit={submit} noValidate>
          <p className="auth-card__note">Enter your email and we'll send reset instructions.</p>
          <label className="auth-field"><span>Email</span><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" /></label>
          <button type="submit" className="auth-btn" disabled={busy}>{busy ? "Sending…" : "Send reset link"}</button>
        </form>
      )}
      <p className="auth-card__foot"><a href="/login">Back to sign in</a></p>
    </div>
  );
}

/** Reset Password: token from URL, set a new password. */
export function ResetPasswordScreen({
  onSubmit,
}: {
  readonly onSubmit: (token: string, password: string) => Promise<{ ok: boolean; error?: string }>;
}): ReactElement {
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setToken(getQueryParam("token")); }, []);
  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault(); setErr(null);
    if (password !== confirm) { setErr("Passwords do not match."); return; }
    setBusy(true);
    const r = await onSubmit(token, password); setBusy(false);
    if (r.ok) setDone(true); else setErr(r.error ?? "Could not reset password.");
  }
  return (
    <div className="auth-card">
      <h1 className="auth-card__title">Choose a new password</h1>
      {done ? (
        <><p className="auth-card__note" role="status">Your password has been reset.</p><p className="auth-card__foot"><a href="/login">Sign in</a></p></>
      ) : (
        <form onSubmit={submit} noValidate>
          {err && <p className="auth-error" role="alert">{err}</p>}
          <label className="auth-field"><span>New password</span><input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" /></label>
          <label className="auth-field"><span>Confirm password</span><input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" /></label>
          <button type="submit" className="auth-btn" disabled={busy}>{busy ? "Saving…" : "Reset password"}</button>
        </form>
      )}
    </div>
  );
}

/**
 * First-login Create Password screen. A must-change-password user signs in with
 * a temporary password and lands here (server + client both route them in). On
 * a fresh page load we don't hold the temp password in memory, so the form
 * collects it explicitly alongside the new password. Submits
 * { currentPassword: tempPw, newPassword } to /api/auth/change-password; on
 * success the server clears must_change_password and we redirect into the app.
 */
export function ChangePasswordScreen({
  onSubmit,
}: {
  readonly onSubmit: (currentPassword: string, newPassword: string) => Promise<{ ok: boolean; code?: string; error?: string }>;
}): ReactElement {
  const [tempPw, setTempPw] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault(); setErr(null);
    if (tempPw === "" || password === "" || confirm === "") { setErr("Please complete all fields."); return; }
    if (password !== confirm) { setErr("Passwords do not match."); return; }
    setBusy(true);
    const r = await onSubmit(tempPw, password); setBusy(false);
    if (r.ok) {
      if (typeof window !== "undefined") window.location.replace("/app/dashboard");
      return;
    }
    if (r.code === "INVALID_CURRENT") setErr("That temporary password is incorrect.");
    else setErr(r.error ?? "Could not update your password.");
  }
  return (
    <div className="auth-card">
      <h1 className="auth-card__title">Create your password</h1>
      <form onSubmit={submit} noValidate data-testid="change-password-form">
        <p className="auth-card__note">Enter the temporary password your administrator gave you, then choose a new password.</p>
        {err && <p className="auth-error" role="alert">{err}</p>}
        <label className="auth-field"><span>Temporary password (from your administrator)</span><input type="password" value={tempPw} onChange={(e) => setTempPw(e.target.value)} autoComplete="current-password" /></label>
        <label className="auth-field"><span>New password</span><input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" /></label>
        <label className="auth-field"><span>Confirm new password</span><input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" /></label>
        <button type="submit" className="auth-btn" disabled={busy}>{busy ? "Saving…" : "Create password"}</button>
      </form>
    </div>
  );
}

/** Invitation Accept: verify token, set password → activate + sign in. */
export function InviteAcceptScreen({
  onLookup,
  onAccept,
}: {
  readonly onLookup: (token: string) => Promise<{ email: string; role: string; expired: boolean } | null>;
  readonly onAccept: (token: string, password: string) => Promise<{ ok: boolean; error?: string }>;
}): ReactElement {
  const [token, setToken] = useState("");
  const [invite, setInvite] = useState<{ email: string; role: string; expired: boolean } | null | "loading" | "missing">("loading");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const t = getQueryParam("token"); setToken(t);
    if (!t) { setInvite("missing"); return; }
    void onLookup(t).then((r) => setInvite(r ?? "missing"));
  }, [onLookup]);
  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault(); setErr(null);
    if (password !== confirm) { setErr("Passwords do not match."); return; }
    setBusy(true);
    const r = await onAccept(token, password); setBusy(false);
    if (!r.ok) setErr(r.error ?? "Could not accept invitation.");
  }
  if (invite === "loading") return <div className="auth-card"><p className="auth-card__note">Verifying invitation…</p></div>;
  if (invite === "missing" || invite === null) return <div className="auth-card"><h1 className="auth-card__title">Invitation not found</h1><p className="auth-card__note">This invitation link is invalid or has expired.</p></div>;
  if (invite.expired) return <div className="auth-card"><h1 className="auth-card__title">Invitation expired</h1><p className="auth-card__note">Ask your administrator to resend the invitation.</p></div>;
  return (
    <div className="auth-card">
      <h1 className="auth-card__title">Welcome to AVOS</h1>
      <p className="auth-card__note">You've been invited as <strong>{invite.role}</strong>. Set a password for <strong>{invite.email}</strong>.</p>
      <form onSubmit={submit} noValidate>
        {err && <p className="auth-error" role="alert">{err}</p>}
        <label className="auth-field"><span>Password (min 10 characters)</span><input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" /></label>
        <label className="auth-field"><span>Confirm password</span><input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" /></label>
        <button type="submit" className="auth-btn" disabled={busy}>{busy ? "Activating…" : "Activate account"}</button>
      </form>
    </div>
  );
}
